'use client'

import { type ReactNode } from 'react'

import { PanelSkeleton } from '@/components/server/product/panel-skeleton'
import { SpecsPanel } from '@/components/server/product/specs-panel'
import { useTranslation } from '@/hooks/client'
import { tw } from '@/lib/utils'
import { formatPrice } from '@/lib/utils/format-price'

import { FeatureTooltipProvider, type TooltipData } from './feature-tooltip'
import { useModelPanels } from './use-model-panels'

interface ModelInfo {
  id: number
  name: string
  fullName: string
  image: string | null
}

interface LevelInfo {
  id: number
  name: string
  price: number | null
}

interface ModelComparisonClientProps {
  models: ModelInfo[]
  levels: LevelInfo[]
  levelsByModel: Record<number, number[]>
  defaultModelId: number
  defaultLevelId: number | undefined
  showAllFeaturesLabel: string
  showLessFeaturesLabel: string
  pricePerPairSuffix: string
  hasExpandableFeatures: boolean
  /** The default (server-rendered) panel's tooltips; fetched panels merge in more. */
  tooltips: Record<string, TooltipData>
  releaseSlug: string
  locale: string
  /** Keys (`${modelId}-${levelId}`) rendered server-side — never fetched. */
  serverPanelKeys: string[]
  /** The server-rendered default panel (all panels in draft mode). */
  children: ReactNode
}

export const ModelComparisonClient = ({
  models,
  levels,
  levelsByModel,
  defaultModelId,
  defaultLevelId,
  showAllFeaturesLabel,
  showLessFeaturesLabel,
  pricePerPairSuffix,
  hasExpandableFeatures,
  tooltips,
  releaseSlug,
  locale,
  serverPanelKeys,
  children,
}: ModelComparisonClientProps) => {
  const { t } = useTranslation()
  const {
    activeModelId,
    activeLevelId,
    expanded,
    panels,
    tooltips: mergedTooltips,
    loadingKey,
    panelsRef,
    selectModel,
    selectLevel,
    toggleFeatures,
    prefetchModel,
    prefetchLevel,
  } = useModelPanels({
    releaseSlug,
    locale,
    levelsByModel,
    defaultModelId,
    defaultLevelId,
    serverPanelKeys,
    initialTooltips: tooltips,
  })

  const validLevelIds = levelsByModel[activeModelId] ?? []
  const visibleLevels = levels.filter((l) => validLevelIds.includes(l.id))
  const isSingleProduct = models.length === 1
  const activeKey = `${activeModelId}-${activeLevelId}`

  return (
    <div
      className={tw(
        '@container/compare flex max-w-[1344px] flex-col',
        !isSingleProduct && 'overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl'
      )}
    >
      <div className={tw(!isSingleProduct && 'px-6 pt-6 pb-6 lg:px-10 lg:pt-8 lg:pb-10')}>
        {/* Model pills */}
        {models.length > 1 && (
          <div className="mb-5">
            <div className="flex flex-wrap gap-2">
              {models.map((model) => (
                <button
                  key={model.id}
                  data-tracking="ignore"
                  onClick={() => selectModel(model.id)}
                  onPointerEnter={() => prefetchModel(model.id)}
                  onFocus={() => prefetchModel(model.id)}
                  className={tw(
                    'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                    model.id === activeModelId
                      ? 'border-lapis/30 bg-lapis/[0.04] ring-lapis text-navy ring-1'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  )}
                >
                  {model.image && <img src={model.image} alt="" className="h-8 w-8 object-contain" loading="lazy" />}
                  <span>{model.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Level pills with prices */}
        {visibleLevels.length > 1 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {visibleLevels.map((lvl) => (
                <button
                  key={lvl.id}
                  data-tracking="ignore"
                  onClick={() => selectLevel(lvl.id)}
                  onPointerEnter={() => prefetchLevel(lvl.id)}
                  onFocus={() => prefetchLevel(lvl.id)}
                  className={tw(
                    'cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors',
                    lvl.id === activeLevelId ? 'bg-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                >
                  {lvl.name}
                  {lvl.price != null && (
                    <span className={tw('ml-1.5', lvl.id === activeLevelId ? 'text-white/70' : 'text-slate-400')}>
                      {formatPrice(lvl.price)} {pricePerPairSuffix}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Panels — default is server-rendered; others are fetched and rendered here,
            all toggled via DOM display by the loader hook. */}
        <FeatureTooltipProvider tooltips={mergedTooltips}>
          <div
            ref={panelsRef}
            data-expanded={expanded ? '' : undefined}
            aria-busy={loadingKey != null ? true : undefined}
            className="[&:not([data-expanded])_[data-expandable]]:hidden"
          >
            {children}
            {Object.values(panels).map((panel) => (
              <SpecsPanel
                key={`${panel.modelId}-${panel.levelId}`}
                {...panel}
                t={t}
                hidden={`${panel.modelId}-${panel.levelId}` !== activeKey}
              />
            ))}
            {loadingKey != null && <PanelSkeleton />}
          </div>
        </FeatureTooltipProvider>

        {/* Show all features toggle */}
        {hasExpandableFeatures && (
          <button
            type="button"
            data-tracking="ignore"
            aria-expanded={expanded}
            data-ht-disclosure-trigger="read-more"
            onClick={toggleFeatures}
            className="mt-6 flex w-full cursor-pointer items-center justify-center gap-4"
          >
            <span className="text-navy text-xl font-light">
              {expanded ? showLessFeaturesLabel : showAllFeaturesLabel}
            </span>
            <div className="bg-navy flex items-center justify-center rounded-full p-[15px]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={tw('transition-transform', expanded ? '-rotate-90' : 'rotate-90')}
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        )}
      </div>
    </div>
  )
}
